import { useState } from 'preact/hooks'
import { composeAuthEmail } from '../lib/auth-email'
import { requireSupabase } from './supabase'

/**
 * Passwordless sign-in: an address, an optional username, then the code sent to
 * it.
 *
 * Ported from `../semainier/src/auth/LoginScreen.tsx` with the strings
 * changed. `shouldCreateUser: false` is deliberate — no account is ever created
 * from this screen. The owner's is created by hand in the Supabase dashboard,
 * and everybody else's by the `invite-member` Edge Function, which sends the
 * code that brings them here. Without that flag, anyone who found the URL could
 * sign themselves up and get an empty but valid account on this project.
 *
 * **The username is what makes a family out of one inbox.** Supabase Auth
 * requires a unique address per account; a household has one mailbox. Léa types
 * `lea` and her parent's address, and `composeAuthEmail` derives the account she
 * actually holds — `papa+lea@x.be` — which is the address the invitation was
 * sent to and the address `verifyOtp` has to be given back. The owner leaves the
 * field empty and signs in exactly as they always did. See
 * `src/lib/auth-email.ts` for why the composition lives in one module.
 */
/** Supabase allows an OTP of 6 to 10 digits; the project decides which. */
const OTP_MAX_LENGTH = 10

/**
 * Keep digits only.
 *
 * Codes are usually pasted straight out of a mail client, which tends to
 * bring a trailing space or a non-breaking space with it. Those make the code
 * fail verification for no visible reason.
 */
export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '').slice(0, OTP_MAX_LENGTH)
}

export function LoginScreen() {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * The address the code goes to, and the address it must be verified against.
   *
   * Held in state rather than recomputed at verification time: the two calls
   * have to agree exactly, and a user who edits the username field while
   * waiting for the mail would otherwise verify against an address the code was
   * never sent to and get "Code incorrect" for a perfectly good code.
   */
  const [authEmail, setAuthEmail] = useState('')

  const preview = composeAuthEmail(email, username)

  async function sendCode(e: Event) {
    e.preventDefault()
    if (!email.trim()) return

    const address = composeAuthEmail(email, username)
    if (!address) {
      setError('Adresse ou nom d’utilisateur invalide.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const { error: sendError } = await requireSupabase().auth.signInWithOtp({
        email: address,
        options: { shouldCreateUser: false },
      })
      if (sendError) throw sendError
      setAuthEmail(address)
      setStep('code')
    } catch (err) {
      setError(
        err instanceof Error
          ? `Impossible d’envoyer le code : ${err.message}`
          : 'Impossible d’envoyer le code.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function verify(e: Event) {
    e.preventDefault()
    if (!code.trim()) return

    setBusy(true)
    setError(null)
    try {
      const { error: verifyError } = await requireSupabase().auth.verifyOtp({
        email: authEmail,
        token: code.trim(),
        // Covers the magic-link code and the invitation code alike: Supabase
        // accepts either token under `email`, which is what lets an invited
        // member finish here rather than needing a screen of their own.
        type: 'email',
      })
      if (verifyError) throw verifyError
      // The session listener in AuthGate takes over from here.
    } catch {
      setError('Code incorrect ou expiré.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main class="login-screen">
      <section class="card login-card">
        <p class="eyebrow">MAISON ZEN</p>

        {step === 'email' ? (
          <form onSubmit={(e) => void sendCode(e)}>
            <h2>Connexion</h2>
            <p class="login-hint">
              Entre ton adresse e-mail : tu recevras un code par e-mail.
            </p>

            <label for="loginEmail">Adresse e-mail</label>
            <input
              id="loginEmail"
              type="email"
              autocomplete="email"
              required
              value={email}
              placeholder="prenom@exemple.be"
              onInput={(e) => setEmail(e.currentTarget.value)}
            />

            <label for="loginUsername">
              Nom d’utilisateur <span class="label-hint">(si on t’en a donné un)</span>
            </label>
            <input
              id="loginUsername"
              type="text"
              autocomplete="username"
              value={username}
              placeholder="lea"
              onInput={(e) => setUsername(e.currentTarget.value)}
            />

            {/* Shown only once it says something the address alone does not, so
                the owner — who has no username — never sees it at all. */}
            {preview && preview !== email.trim().toLowerCase() && (
              <p class="login-hint">
                Ton compte : <b>{preview}</b>
              </p>
            )}

            {error && <p class="login-error">{error}</p>}

            <button class="primary" type="submit" disabled={busy}>
              {busy ? 'Envoi…' : 'Recevoir mon code'}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void verify(e)}>
            <h2>Code reçu</h2>
            <p class="login-hint">
              Un code a été envoyé à <b>{authEmail}</b>.
            </p>

            <label for="loginCode">Code reçu par e-mail</label>
            <input
              id="loginCode"
              inputMode="numeric"
              autocomplete="one-time-code"
              // Supabase's OTP length is a project setting, anywhere from 6 to
              // 10 digits. Hardcoding a length here silently truncates a
              // pasted code and leaves the user staring at a valid-looking
              // field that will not verify.
              maxLength={OTP_MAX_LENGTH}
              required
              value={code}
              placeholder="Ton code"
              onInput={(e) => setCode(digitsOnly(e.currentTarget.value))}
            />

            {error && <p class="login-error">{error}</p>}

            <button class="primary" type="submit" disabled={busy}>
              {busy ? 'Vérification…' : 'Me connecter'}
            </button>
            <button
              class="secondary"
              type="button"
              onClick={() => {
                setStep('email')
                setCode('')
                setError(null)
              }}
            >
              Changer d’adresse
            </button>
          </form>
        )}
      </section>
    </main>
  )
}
